import FileUploadIcon from "@mui/icons-material/FileUploadOutlined";
import { Box, Button, Stack, Typography } from "@mui/material";
import { ChangeEvent, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { IBodyShellImageSlot } from "../../../../services/bodyshellService";
import {
  containerSx,
  fieldLabelSx,
  fileNameSx,
  groupTitleSx,
  instructionSx,
} from "./StepImages.styles";

export type BodyShellImageValues = Partial<Record<IBodyShellImageSlot, string>>;

export type BodyShellImageFileNames = Partial<
  Record<IBodyShellImageSlot, string>
>;

interface IStepImages {
  values: BodyShellImageValues;
  onChange: (values: BodyShellImageValues) => void;
  onValidChange: (valid: boolean) => void;
  onFileNameChange: (slot: IBodyShellImageSlot, fileName: string) => void;
  onError?: (message: string) => void;
}

const MAX_FILE_SIZE = 2 * 1024 * 1024;

const REQUIRED_SLOTS: IBodyShellImageSlot[] = [
  "car",
  "frontLeftC",
  "frontLeft",
  "frontRightC",
  "frontRight",
  "rearLeftC",
  "rearLeft",
  "rearRightC",
  "rearRight",
  "frontLeftCOw",
  "frontRightCOw",
  "rearLeftCOw",
  "rearRightCOw",
  "trunk",
  "hood",
];

const ALL_SLOTS: IBodyShellImageSlot[] = [
  ...REQUIRED_SLOTS,
  "sunRoofC",
  "sunRoof",
];

const GROUPS: { titleKey: string; slots: IBodyShellImageSlot[] }[] = [
  { titleKey: "car", slots: ["car"] },
  {
    titleKey: "frontDoors",
    slots: ["frontLeftC", "frontLeft", "frontRightC", "frontRight"],
  },
  {
    titleKey: "rearDoors",
    slots: ["rearLeftC", "rearLeft", "rearRightC", "rearRight"],
  },
  {
    titleKey: "windows",
    slots: ["frontLeftCOw", "frontRightCOw", "rearLeftCOw", "rearRightCOw"],
  },
  { titleKey: "trunkHood", slots: ["trunk", "hood"] },
  { titleKey: "sunRoof", slots: ["sunRoofC", "sunRoof"] },
];

function getFileError(file: File, t: (key: string) => string) {
  if (!/\.png$/i.test(file.name) || (file.type && file.type !== "image/png")) {
    return t("bodyshell.step2.invalidFormat");
  }
  if (file.size > MAX_FILE_SIZE) {
    return t("bodyshell.step2.maxSize");
  }
  return "";
}

export default function StepImages({
  values,
  onChange,
  onValidChange,
  onFileNameChange,
  onError,
}: IStepImages) {
  const { t } = useTranslation();
  const [fileNames, setFileNames] = useState<
    Partial<Record<IBodyShellImageSlot, string>>
  >({});

  const firstEmptyRequiredIndex = useMemo(
    () => REQUIRED_SLOTS.findIndex((slot) => !values[slot]),
    [values],
  );
  const allRequiredFilled = firstEmptyRequiredIndex === -1;
  const firstEmptySlotIndex = useMemo(
    () => ALL_SLOTS.findIndex((slot) => !values[slot]),
    [values],
  );

  useEffect(() => {
    onValidChange(allRequiredFilled);
  }, [allRequiredFilled, onValidChange]);

  const isDisabled = (slot: IBodyShellImageSlot) => {
    if (values[slot]) return false;
    const slotIndex = ALL_SLOTS.indexOf(slot);
    const nextSlotIndex = allRequiredFilled
      ? firstEmptySlotIndex
      : firstEmptyRequiredIndex;
    return slotIndex > nextSlotIndex;
  };

  const handleFileChange = (
    slot: IBodyShellImageSlot,
    event: ChangeEvent<HTMLInputElement>,
  ) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    const error = getFileError(file, t);
    if (error) {
      onError?.(error);
      return;
    }

    const reader = new FileReader();
    reader.onload = () => {
      const result = String(reader.result ?? "");
      const base64 = result.split(",")[1];
      if (!base64) {
        onError?.(t("bodyshell.step2.readError"));
        return;
      }
      setFileNames((previous) => ({ ...previous, [slot]: file.name }));
      onChange({ ...values, [slot]: base64 });
      onFileNameChange(slot, file.name);
    };
    reader.onerror = () => {
      onError?.(t("bodyshell.step2.readError"));
    };
    reader.readAsDataURL(file);
  };

  return (
    <Box sx={containerSx}>
      <Typography sx={instructionSx}>
        {t("bodyshell.step2.instruction")}
      </Typography>
      {GROUPS.map((group, groupIndex) => (
        <Box
          key={group.titleKey}
          sx={{ borderBottom: "1px solid #DFE4E8", py: 1.5 }}
        >
          <Typography variant="subtitle2" sx={groupTitleSx}>
            {groupIndex + 1}. {t(`bodyshell.step2.groups.${group.titleKey}`)}
          </Typography>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            sx={{ flexWrap: "wrap", gap: 1.5 }}
          >
            {group.slots.map((slot) => {
              const inputId = `bodyshell-image-${slot}`;
              const disabled = isDisabled(slot);
              return (
                <Box key={slot} sx={{ flex: "1 1 45%", minWidth: 300 }}>
                  <Stack
                    direction="row"
                    spacing={1}
                    sx={{ alignItems: "center", minWidth: 0 }}
                  >
                    <Typography variant="body2" sx={fieldLabelSx}>
                      {t(`bodyshell.step2.fields.${slot}`)} :
                    </Typography>
                    <Button
                      component="label"
                      htmlFor={inputId}
                      variant="contained"
                      size="small"
                      aria-label={t("bodyshell.step2.replace")}
                      title={t("bodyshell.step2.replace")}
                      disabled={disabled}
                      sx={{ minWidth: 32, width: 32, height: 32, p: 0 }}
                    >
                      <FileUploadIcon fontSize="small" />
                      <input
                        id={inputId}
                        hidden
                        type="file"
                        accept=".png,image/png"
                        onChange={(event) => handleFileChange(slot, event)}
                      />
                    </Button>
                    <Typography variant="caption" noWrap sx={fileNameSx}>
                      {fileNames[slot] ?? values[slot]}
                    </Typography>
                  </Stack>
                </Box>
              );
            })}
          </Stack>
        </Box>
      ))}
      <Typography variant="caption" sx={{ display: "block", mt: 1 }}>
        {t("bodyshell.step2.requiredNote")}
      </Typography>
    </Box>
  );
}
